<script setup lang="ts">
import {Checkbox, RadioButton} from "@/Components/Library";
import Column from "primevue/column";
import DataTable from "primevue/datatable";

const props = defineProps<{
    question: {[key: string]: any; options: any[]};
    disabled?: boolean;
}>();

const model = defineModel<Record<string, string[]>>();

const selectedCategories = (optionId: string): string[] => {
    return model.value[optionId] ?? [];
};

const setSelectedCategories = (optionId: string, categories: string[]): void => {
    if (categories.length) {
        model.value[optionId] = categories;
    } else {
        delete model.value[optionId];
    }
};

const selectedOption = (categoryId: string): string | null => {
    return props.question.options.find((option) => selectedCategories(option.id).includes(categoryId))?.id ?? null;
};

const setSelectedOption = (categoryId: string, optionId: string): void => {
    const previousOptionId = selectedOption(categoryId);

    if (previousOptionId) {
        setSelectedCategories(
            previousOptionId,
            selectedCategories(previousOptionId).filter((selectedCategoryId) => selectedCategoryId !== categoryId),
        );
    }

    setSelectedCategories(optionId, [...selectedCategories(optionId), categoryId]);
};
</script>
<template>
    <div>
        <h3 class="mb-3 mt-1 px-1 text-sm font-bold">
            Select {{ question.params.allow_multiple ? "one or more options" : "one option" }} for each category
        </h3>
        <DataTable :value="question.options" dataKey="id" scrollable size="small" tableStyle="min-width: max-content">
            <Column
                field="value"
                header="Options"
                frozen
                headerClass="max-w-42 whitespace-normal break-words"
                bodyClass="max-w-42 whitespace-normal break-words"
                headerStyle="max-width: 16rem"
                bodyStyle="max-width: 16rem"
            >
                <template #body="{data: option}">
                    {{ option.value }}
                </template>
            </Column>
            <Column
                v-for="category in question.categories"
                :key="category.id"
                :field="category.id"
                headerClass="text-center"
                bodyClass="text-center"
                style="min-width: 7rem"
            >
                <template #header>
                    <span class="max-w-42 mx-auto block whitespace-normal break-words">{{ category.name }}</span>
                </template>
                <template #body="{data: option}">
                    <div class="text-center">
                        <Checkbox
                            v-if="question.params.allow_multiple"
                            :model-value="selectedCategories(option.id)"
                            :value="category.id"
                            :input-id="`matrix-${option.id}-${category.id}`"
                            :name="`matrix-${option.id}`"
                            :disabled="disabled"
                            @update:model-value="setSelectedCategories(option.id, $event)"
                        />
                        <RadioButton
                            v-else
                            :model-value="selectedOption(category.id)"
                            :value="option.id"
                            :input-id="`matrix-${option.id}-${category.id}`"
                            :name="`matrix-${category.id}`"
                            :disabled="disabled"
                            @update:model-value="setSelectedOption(category.id, $event)"
                        />
                        <label :for="`matrix-${option.id}-${category.id}`" class="sr-only">
                            {{ option.value }}: {{ category.name }}
                        </label>
                    </div>
                </template>
            </Column>
        </DataTable>
    </div>
</template>
<style scoped></style>
